/**
 * Partículas de "pó de serragem" do painel do fluxo — porte direto do
 * startDust()/stopDust() do design.
 *
 * Um único <canvas> (não elementos DOM: com centenas deles a renderização
 * trava). 150 pontos na cor --a, sobem 5–18 px/s, balançam em senoide e
 * cintilam; ~18% maiores ganham halo. DPR limitado a 1,5. Pausa fora da tela
 * (IntersectionObserver) e com a aba oculta; estático com reduced-motion.
 *
 * Devolve a função que para tudo (rAF + observers).
 */

const QUANTIDADE = 150;
const DPR_MAXIMO = 1.5;
const SEMENTE = 11;
const VOLTA = 6.2832;

// mulberry32: sequência determinística, a mesma do design.
function geradorComSemente(semente) {
  let a = semente;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function iniciarPoeira(canvas) {
  if (!canvas || typeof canvas.getContext !== "function") return () => {};
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};

  let rodando = true;
  let raf = 0;
  const rnd = geradorComSemente(SEMENTE);
  const pontos = Array.from({ length: QUANTIDADE }, () => ({
    x: rnd(), y: rnd(), z: rnd(), ph: rnd() * VOLTA, sw: 0.5 + rnd() * 1.2, tw: 0.6 + rnd() * 1.4,
  }));
  const win = canvas.ownerDocument.defaultView || window;
  const reduzir = Boolean(win.matchMedia && win.matchMedia("(prefers-reduced-motion: reduce)").matches);
  let w = 0;
  let h = 0;
  let dpr = 1;
  let cor = "#888";
  let k = 1;
  let visivel = true;
  let quadro = 0;
  let ultimo = 0;

  const medir = () => {
    const r = canvas.getBoundingClientRect();
    dpr = Math.min(win.devicePixelRatio || 1, DPR_MAXIMO);
    w = canvas.clientWidth || r.width;
    h = canvas.clientHeight || r.height;
    canvas.width = Math.max(1, Math.round(w * dpr));
    canvas.height = Math.max(1, Math.round(h * dpr));
  };
  // A cor vem do CSS (--a e --dk mudam com o tema), relida a cada 30 quadros.
  const lerCor = () => {
    const cs = win.getComputedStyle(canvas);
    const c = cs.getPropertyValue("--a").trim();
    if (c) cor = c;
    const kk = parseFloat(cs.getPropertyValue("--dk"));
    k = Number.isFinite(kk) ? kk : 1;
  };
  medir();
  lerCor();

  const ro = win.ResizeObserver ? new win.ResizeObserver(medir) : null;
  if (ro) ro.observe(canvas);
  const io = win.IntersectionObserver
    ? new win.IntersectionObserver((es) => {
      visivel = es.length ? es[es.length - 1].isIntersecting : true;
    })
    : null;
  if (io) io.observe(canvas);

  const desenhar = (t) => {
    if (!rodando) return;
    raf = win.requestAnimationFrame(desenhar);
    const dt = ultimo ? Math.min(0.05, (t - ultimo) / 1000) : 0;
    ultimo = t;
    if (!visivel || win.document.hidden || w < 2 || h < 2) return;
    if (quadro++ % 30 === 0) lerCor();
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = cor;
    const s = t / 1000;
    for (let i = 0; i < pontos.length; i++) {
      const p = pontos[i];
      if (!reduzir) {
        p.y -= ((5 + p.z * 13) * dt) / h;
        if (p.y < -0.03) {
          p.y = 1.03;
          p.x = rnd();
        }
      }
      const balanco = reduzir
        ? 0
        : Math.sin(s * 0.35 * p.sw + p.ph) * (8 + p.z * 16) + Math.sin(s * 0.9 * p.sw + p.ph * 2) * 3;
      const x = p.x * w + balanco;
      const y = p.y * h;
      const r = 0.6 + p.z * 1.7;
      const borda = Math.min(1, p.y * 6, (1 - p.y) * 6);
      const cintilar = reduzir ? 1 : 0.6 + 0.4 * Math.sin(s * p.tw + p.ph);
      const alfa = Math.max(0, (0.22 + p.z * 0.55) * cintilar * borda * k);
      if (alfa <= 0.01) continue;
      if (p.z > 0.82) {
        ctx.globalAlpha = alfa * 0.18;
        ctx.beginPath();
        ctx.arc(x, y, r * 3.2, 0, VOLTA);
        ctx.fill();
      }
      ctx.globalAlpha = alfa;
      ctx.beginPath();
      ctx.arc(x, y, r, 0, VOLTA);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  };
  raf = win.requestAnimationFrame(desenhar);

  return () => {
    rodando = false;
    if (raf) win.cancelAnimationFrame(raf);
    if (ro) ro.disconnect();
    if (io) io.disconnect();
  };
}
